import { WriteTransaction } from 'replicache'
import {
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  ComponentBlock,
  InstanceBlock,
  Block,
  isRichTextBlock,
  RichTextBlock,
  isListBlock,
  blockType,
  isReferenceBlock,
  isRootBlock,
  ListBlock,
  normalizeStringPath,
  BlockType,
  ReferenceBlock,
  blockRelationshipBaseOps,
  isVirtualInstanceChildTarget,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { getIdempotentId } from '@basehub/utils/id/index'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import { updateBlock, duplicateBlock } from './crud'
import { detachBlocksFromTarget } from './reference'
import { isInstanceInRichText } from '@basehub/tiptap-api-utils/rich-text-helpers'
import { z } from 'zod'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { doCopyOnWrite, idToSourceBlockId } from './copy-on-write'
import cloneDeep from 'lodash/cloneDeep'
import { BlockBatchMutationParams } from './batch'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { tryCatch } from '@basehub/utils'

/* -------------------------------------------------------------------------------------------------
 * Sync all rich texts that use this component
 * -----------------------------------------------------------------------------------------------*/

async function syncAllRichTextsUsingComponent(
  tx: WriteTransaction,
  {
    rootBlockId,
    componentBlockId: _componentBlockId,
    isoNow,
  }: {
    rootBlockId: string
    componentBlockId: string
    isoNow: string
    expectNotFoundForBlockIds?: string[]
    prefetchedTreeManager?: TreeManager<Block, NonNullable<unknown>>
  }
) {
  ensureBaseHubTransaction(tx)
  // 1. build tree
  const treeManager = await tx.getTreeManager(rootBlockId)
  const componentNode = treeManager.getNode(_componentBlockId, {
    required: true,
  })
  const blocksByType = (treeManager.compoundIndexes.get('blocksByType') ??
    new Map()) as Map<BlockType, Set<string>>

  // 2. get all rich texts targeting component
  const richTextsTargetingComponent = []
  for (const id of blocksByType.get('rich-text') ?? new Set()) {
    const n = treeManager.getNode(id, { required: true })
    if (
      // @ts-ignore
      n.value.__virtual
    ) {
      continue
    }
    if (isRichTextBlock(n.value)) {
      if (
        n.value.constraints.allowedComponents?.some(
          (allowedComponentId) =>
            idToSourceBlockId(componentNode.value) === allowedComponentId
        ) ||
        n.value.constraints.allowedInternalLinks?.some(
          (allowedComponentId) =>
            idToSourceBlockId(componentNode.value) === allowedComponentId
        )
      ) {
        richTextsTargetingComponent.push(n as TreeNode<RichTextBlock>)
      }
    }
  }

  const undo: BlockBatchMutationParams['mutations'] = []

  for (const richTextNode of richTextsTargetingComponent) {
    const newAllowedComponents =
      richTextNode.value.constraints.allowedComponents?.filter(
        (allowedComponentId) =>
          allowedComponentId !== idToSourceBlockId(componentNode.value)
      ) ?? []
    const newAllowedInternalLinks =
      richTextNode.value.constraints.allowedInternalLinks?.filter(
        (allowedComponentId) =>
          allowedComponentId !== idToSourceBlockId(componentNode.value)
      ) ?? []

    const { undo: undoForThis } = await updateBlock(tx, {
      id: richTextNode.value.id,
      data: {
        type: richTextNode.value.type,
        constraints: {
          allowedComponents: newAllowedComponents,
          allowedInternalLinks: newAllowedInternalLinks,
        },
      },
      instanceChildTarget: richTextNode.value.instanceChildTarget ?? null,
      path: treeManager.getPath(richTextNode.key, {
        format: 'string',
        required: true,
      }),
      strategy: 'merge-deep',
      isoNow,
    })

    if (undoForThis) {
      undo.push(...undoForThis)
    }
  }

  return { undo }
}

/* -------------------------------------------------------------------------------------------------
 * Toggle make component
 * -----------------------------------------------------------------------------------------------*/

export type ToggleMakeComponentParams = {
  value: boolean
  blockId: string
  path: string
  initialTypeOptions?: ComponentBlock['typeOptions']
  idSeed: string
  isoNow: string
}

async function toggleMakeComponent(
  tx: WriteTransaction,
  {
    value,
    blockId,
    path,
    initialTypeOptions,
    idSeed,
    isoNow,
  }: WithServerSecret<ToggleMakeComponentParams>
) {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const splittedPath = normalizeStringPath({
    path,
    edgeId: blockId,
    format: 'array',
  })
  const rootBlockId = splittedPath[0]
  invariant(rootBlockId, `rootBlockId not found`)

  const b = await blockBaseOps.get(tx, blockId)
  invariant(b, `block ${blockId} not found`)
  const children = await tx.getBlockChildren({
    sourceBlockId: idToSourceBlockId(b),
    rootBlockId,
  })

  const undo: BlockBatchMutationParams['mutations'] = []

  if (value) {
    // make component
    invariant(isDocumentBlock(b), `block ${blockId} is not a document block`)
    const { undo: undoMakeComp } = await updateBlock(tx, {
      id: b.id,
      data: {
        type: 'component',
        typeOptions: initialTypeOptions ?? {
          displayTitle: '',
          displayDescription: '',
          displayIcon: '',
        },
      },
      instanceChildTarget: b.instanceChildTarget ?? null,
      path,
      strategy: 'merge',
      isoNow,
    })

    undo.push(...undoMakeComp)

    const convertChildrenThatAreDocumentsIntoComponentsPromise = children.map(
      async (child) => {
        const childBlock = await blockBaseOps.get(tx, child.blockId)
        if (!childBlock || !isDocumentBlock(childBlock)) return

        const pathForThisDocument = normalizeStringPath({
          path,
          edgeId: childBlock.id,
          format: 'string',
          includeEdge: true,
        })

        const scopedIdSeed = getIdempotentId({
          idSeed,
          splitPaths: {
            newPath: `toggle-make:convert-docs:child-${child.id}`,
          },
        })

        const result = await toggleMakeComponent(tx, {
          value: true,
          blockId: childBlock.id,
          path: pathForThisDocument,
          initialTypeOptions: {
            displayTitle: '',
            displayDescription: '',
            displayIcon: '',
          },
          idSeed: scopedIdSeed,
          isoNow,
        })
        if (result.undo) {
          undo.push(...result.undo)
        }
        return result
      }
    )

    await Promise.all(convertChildrenThatAreDocumentsIntoComponentsPromise)
  } else {
    // make document
    invariant(isComponentBlock(b), `block ${blockId} is not a component block`)
    const splittedPath = normalizeStringPath({
      path,
      edgeId: blockId,
      includeEdge: false,
    })
    const rootBlockId = splittedPath[0]
    invariant(rootBlockId, `rootBlockId not found`)
    await checkComponentDeletion(tx, {
      component: b,
      rootBlockId,
    })
    const detachInstancesResult = await detachInstancesOfComponent(tx, {
      sourceBlockId: idToSourceBlockId(b),
      rootBlockId,
      idSeed,
      isoNow,
    })
    if (detachInstancesResult?.undo) {
      undo.push(...detachInstancesResult.undo)
    }

    const detatchFromTargetsResult = await detachBlocksFromTarget(tx, {
      isoNow,
      sourceBlockId: idToSourceBlockId(b),
      rootBlockId,
    })
    if (detatchFromTargetsResult.undo) {
      undo.push(...detatchFromTargetsResult.undo)
    }

    const { undo: undoMakeDoc } = await updateBlock(tx, {
      id: b.id,
      data: {
        type: 'document',
        typeOptions: {},
      },
      instanceChildTarget: b.instanceChildTarget ?? null,
      path,
      strategy: 'merge',
      isoNow,
    })
    undo.push(...undoMakeDoc)
  }

  return { undo }
}

/* -------------------------------------------------------------------------------------------------
 * Detatch instances of component
 * -----------------------------------------------------------------------------------------------*/

const DEBUG = false

async function detachInstancesOfComponent(
  tx: WriteTransaction,
  {
    sourceBlockId,
    rootBlockId,
    isoNow,
    idSeed,
    nonPromotableBlockIds,
  }: {
    sourceBlockId: string
    rootBlockId: string
    isoNow: string
    idSeed: string
    nonPromotableBlockIds?: string[]
  }
) {
  ensureBaseHubTransaction(tx)
  const treeManager = await tx.getTreeManager(rootBlockId)
  const componentNode = treeManager.getNode(sourceBlockId)
  if (!componentNode) {
    throw new Error(`block ${sourceBlockId} not found`)
  }
  const component = componentNode.value
  invariant(
    isComponentBlock(component),
    `block ${sourceBlockId} is not a component`
  )
  const componentChildren = treeManager.getChildrenKeys(componentNode.key)
  const componentParent = componentNode.parentKey
    ? (treeManager.getNode(componentNode.parentKey)?.value ?? null)
    : null

  const componentSourceId = idToSourceBlockId(componentNode.value)

  // 2. get all instances of component
  const instancesOfComponent =
    (
      (treeManager.compoundIndexes.get('componentInstances') ??
        new Map()) as Map<string, Set<string>>
    ).get(componentSourceId) ?? new Set()
  const collectionsOfComponent =
    (
      (treeManager.compoundIndexes.get('componentCollections') ??
        new Map()) as Map<string, Set<string>>
    ).get(componentSourceId) ?? new Set()

  if (instancesOfComponent.size === 0 && collectionsOfComponent.size === 0) {
    return // nothing to do here
  }

  const undo: BlockBatchMutationParams['mutations'] = []

  let instanceToPromote:
    | { block: InstanceBlock; parent: Block | null; path: string }
    | undefined

  let collectionToPromote: { block: ListBlock; path: string } | undefined

  const usedUnderInstances: Block[] = []
  for (const instanceId of instancesOfComponent) {
    const node = treeManager.getNode(instanceId)
    if (!node) continue
    const pathResult = tryCatch(() => {
      return treeManager.getPath(instanceId, {
        format: 'array',
        required: true,
      })
    })

    if (!pathResult.success) {
      // we assume the instance is being deleted
      continue
    }

    const path = pathResult.result

    if (path.some((id) => nonPromotableBlockIds?.includes(id))) {
      continue
    }

    const parentNode = node.parentKey
      ? treeManager.getNode(node.parentKey, { required: true })
      : null

    if (parentNode) {
      if (
        isComponentBlock(parentNode.value) ||
        isDocumentBlock(parentNode.value) ||
        isRootBlock(parentNode.value)
      ) {
        const {
          block: instanceToPromoteBlock,
          parentBlock: instanceToPromoteParent,
          path: instanceToPromotePath,
        } = await doCopyOnWrite(tx, {
          partialBlock: {
            ...node.value,
            instanceChildTarget: node.value.instanceChildTarget,
          },
          path: treeManager.getPath(node.value.id, {
            format: 'string',
            required: true,
          }),
          isoNow,
        })
        invariant(isInstanceBlock(instanceToPromoteBlock), 'expected instance')
        instanceToPromote = {
          block: cloneDeep(instanceToPromoteBlock),
          parent:
            'value' in instanceToPromoteParent ? instanceToPromoteParent : null,
          path: instanceToPromotePath,
        }
      } else if (isInstanceBlock(parentNode.value)) {
        if (
          componentParent &&
          parentNode.value.typeOptions.targetBlockId ===
            idToSourceBlockId(componentParent)
        ) {
          /**
           * If the parent of the instance targets the parent of the component, it means that it'll be deleted after (parent) instance re-sync.
           * so we wouldn't wanna throw an error for this block.
           */
          continue
        }
        usedUnderInstances.push(parentNode.value)
      }
    }
  }

  if (!instanceToPromote) {
    // see if there's a collection to promote
    for (const collectionId of collectionsOfComponent) {
      const node = treeManager.getNode(collectionId)
      if (!node) continue
      invariant(isListBlock(node.value), `expected list block`)

      if (componentNode.parentKey === collectionId) {
        // this collection is the parent of the component, so we can't promote it.
        continue
      }

      const pathResult = tryCatch(() => {
        return treeManager.getPath(collectionId, {
          format: 'array',
          required: true,
        })
      })

      if (!pathResult.success) {
        // we assume the collection is being deleted
        continue
      }

      const path = pathResult.result

      if (path.some((id) => nonPromotableBlockIds?.includes(id))) {
        continue
      }

      const parentNode = node.parentKey
        ? treeManager.getNode(node.parentKey, { required: true })
        : null

      if (parentNode) {
        if (
          isComponentBlock(parentNode.value) ||
          isDocumentBlock(parentNode.value) ||
          isRootBlock(parentNode.value)
        ) {
          const {
            block: collectionToPromoteBlock,
            path: collectionToPromotePath,
          } = await doCopyOnWrite(tx, {
            partialBlock: {
              ...node.value,
              instanceChildTarget: node.value.instanceChildTarget,
            },
            path: treeManager.getPath(collectionId, {
              format: 'string',
              required: true,
            }),
            isoNow,
          })
          invariant(isListBlock(collectionToPromoteBlock), 'expected list')

          collectionToPromote = {
            block: cloneDeep(collectionToPromoteBlock),
            path: collectionToPromotePath,
          }
        }
      }
    }
  }

  if (!instanceToPromote && !collectionToPromote) {
    if (usedUnderInstances.length > 0) {
      throw new MutatorUserError(
        `Error while trying to promote an instance of the "${component.title}" component. Block is being used under ${usedUnderInstances.length} instances.`
      )
    } else {
      return // ignore, none affected
    }
  }

  let promoted: ComponentBlock | null = null
  const newChildrenMapToTarget = new Map<string, string>()
  if (DEBUG) console.log({ instanceToPromote, instancesOfComponent })

  if (instanceToPromote) {
    const instanceChildren = treeManager
      .getChildrenKeys(instanceToPromote.block.id)
      .map((key) => {
        const node = treeManager.getNode(key, { required: true })
        return node
      })
    await Promise.all(
      componentChildren.map(async (cId) => {
        const compChild = treeManager.getNode(cId, { required: true })

        if (compChild.value.type === 'comment') {
          // we duplicate comments, as we were referencing them directly.
          const duplicatedBlockResult = await duplicateBlock(tx, {
            duplicatedBlockId: compChild.value.id,
            idSeed: (idSeed = getIdempotentId({ idSeed })),
            isoNow,
            path: treeManager.getPath(compChild.value.id, {
              format: 'string',
              required: true,
            }),
            titleUpdateStrategy: 'equal',
            newBlockId: (idSeed = getIdempotentId({ idSeed })),
          })
          const duplicate = duplicatedBlockResult?.newBlock
          if (duplicatedBlockResult?.undo) {
            if (
              duplicatedBlockResult.newBlockRelationship &&
              !duplicatedBlockResult.newBlockRelationship.structural
            ) {
              // child will now be a child of the component, and therefore "structural"
              // no need to undo this, as undo will delete it
              await blockRelationshipBaseOps.update(tx, {
                id: duplicatedBlockResult.newBlockRelationship.id,
                structural: true,
              })
            }
            undo.push(...duplicatedBlockResult.undo)
          }
          invariant(duplicate, `duplicate not found`)
          newChildrenMapToTarget.set(
            idToSourceBlockId(compChild.value),
            idToSourceBlockId(duplicate)
          )
          return
        }

        const _instanceChild = instanceChildren.find(
          (c) =>
            c.value.instanceChildTarget === idToSourceBlockId(compChild.value)
        )
        invariant(_instanceChild, `_instanceChild not found`)

        const {
          updatedBlock: instanceChild,
          br: instanceChildBr,
          undo: updateUndo,
        } = await updateBlock(tx, {
          id: _instanceChild.value.id,
          instanceChildTarget: _instanceChild.value.instanceChildTarget,
          data: {
            type: _instanceChild.value.type,
            instanceChildTarget: null,
          },
          path: treeManager.getPath(_instanceChild, {
            format: 'string',
            required: true,
          }),
          strategy: 'merge-deep',
          isoNow,
        })

        undo.push(...updateUndo)
        treeManager.updateNode(instanceChild.id, { value: instanceChild })

        if (!instanceChildBr.structural) {
          // child will now be a child of the component, and therefore "structural"
          await blockRelationshipBaseOps.update(tx, {
            id: instanceChildBr.id,
            structural: true,
          })
          undo.push({
            op: 'updateBR',
            args: { id: instanceChildBr.id, structural: false },
          })
        }

        newChildrenMapToTarget.set(
          idToSourceBlockId(compChild.value),
          idToSourceBlockId(instanceChild)
        )
      })
    )
    promoted = {
      ...instanceToPromote.block,
      id: instanceToPromote.block.id,
      type: 'component',
      typeOptions: component.typeOptions,
      previewUrl: instanceToPromote.block.previewUrl ?? component.previewUrl,
      searchConfig:
        instanceToPromote.block.searchConfig ?? component.searchConfig,
    }
    /**
     * promote instance to component
     *
     * this process involves changing the type of the instance to component and overriding some of its properties
     * also, "enhancing" and updating its children, and adding the comments in there.
     */
    if (promoted) {
      const { undo: updateUndo } = await updateBlock(tx, {
        id: promoted.id,
        data: promoted,
        strategy: 'replace',
        instanceChildTarget: promoted.instanceChildTarget,
        isoNow,
        path: treeManager.getPath(promoted.id, {
          format: 'string',
          required: true,
        }),
        ignoreHashComparison: true,
        skipPropagateHiddenState: true,
      })
      undo.push(...updateUndo)
      treeManager.updateNode(promoted.id, { value: promoted })
    }
  } else if (collectionToPromote) {
    const duplicatedComponentId = (idSeed = getIdempotentId({ idSeed }))

    /**
     * First we'll update the collection to point to the new component we'll create.
     */
    const { undo: updateCollectionUndo, updatedBlock: updatedCollection } =
      await updateBlock(tx, {
        id: collectionToPromote.block.id,
        instanceChildTarget: collectionToPromote.block.instanceChildTarget,
        data: {
          type: 'list',
          typeOptions: { targetBlockId: duplicatedComponentId },
        },
        strategy: 'merge-deep',
        isoNow,
        path: collectionToPromote.path,
      })
    undo.push(...updateCollectionUndo)
    treeManager.updateNode(updatedCollection.id, {
      value: updatedCollection,
    })

    /**
     * We'll create a new component under this colleciton and make it the promoted one.
     */
    const duplicatedComponentResult = await duplicateBlock(tx, {
      duplicatedBlockId: component.id,
      idSeed: (idSeed = getIdempotentId({ idSeed })),
      isoNow,
      newBlockId: duplicatedComponentId,
      path: collectionToPromote.path,
      duplicatedComponentStrategy: 'create-component',
      titleUpdateStrategy: 'equal',
    })
    const duplicatedComponent = duplicatedComponentResult?.newBlock

    if (duplicatedComponentResult?.undo) {
      undo.push(...duplicatedComponentResult.undo)
    }
    invariant(duplicatedComponent, `duplicatedComponent not found`)
    invariant(
      isComponentBlock(duplicatedComponent),
      `expected duplicatedComponent ${duplicatedComponent.title} to be a component block`
    )
    const originalComponentChildren = treeManager.getChildrenKeys(component.id)
    const duplicatedComponentChildren = treeManager.getChildrenKeys(
      duplicatedComponent.id
    )
    if (!duplicatedComponent.hidden) {
      const { undo: updateUndo, updatedBlock: updatedDuplicatedComponent } =
        await updateBlock(tx, {
          id: duplicatedComponent.id,
          instanceChildTarget: duplicatedComponent.instanceChildTarget,
          data: { type: duplicatedComponent.type, hidden: true },
          strategy: 'merge',
          isoNow,
          path: duplicatedComponentResult.newBlockPath,
          ignoreHashComparison: true,
          skipPropagateHiddenState: true,
        })
      undo.push(...updateUndo)
      treeManager.updateNode(updatedDuplicatedComponent.id, {
        value: updatedDuplicatedComponent,
      })
    }
    originalComponentChildren.forEach((cId, i) => {
      const newChildId = duplicatedComponentChildren[i]
      invariant(newChildId, `newChildId not found`)
      newChildrenMapToTarget.set(
        idToSourceBlockId(cId, treeManager),
        idToSourceBlockId(newChildId, treeManager)
      )
    })
    promoted = duplicatedComponent
  }

  invariant(promoted, `promoted should be defined by this point`)

  // 3. retarget instances to promoted component
  for (const _instanceId of instancesOfComponent) {
    let instanceId = _instanceId
    if (instanceId === idToSourceBlockId(promoted)) continue // skip the promoted one

    const node = treeManager.getNode(instanceId)
    if (!node) continue
    if (isVirtualInstanceChildTarget(node.value)) continue
    if (!isInstanceBlock(node.value)) continue

    let path = treeManager.getPath(node, {
      format: 'string',
      required: true,
    })

    if (
      !(
        // @ts-ignore
        node.value.__virtual
      )
    ) {
      const {
        undo: undoForThis,
        updatedBlock,
        updatedPath,
      } = await updateBlock(tx, {
        id: instanceId,
        data: {
          type: 'instance',
          typeOptions: {
            ...node.value.typeOptions,
            targetBlockId: idToSourceBlockId(promoted),
          },
        },
        instanceChildTarget: node.value.instanceChildTarget,
        strategy: 'merge',
        path,
        isoNow,
        ignoreHashComparison: true,
      })

      undo.push(...undoForThis)
      path = updatedPath
      instanceId = updatedBlock.id
      treeManager.updateNode(instanceId, { value: updatedBlock })
    }

    const nodeChildren = treeManager.getChildrenKeys(instanceId)

    await Promise.all(
      nodeChildren.map(async (key) => {
        const nodeChild = treeManager.getNode(key)
        if (
          !nodeChild ||
          // @ts-ignore
          nodeChild.value.__virtual
        ) {
          return
        }
        invariant(
          nodeChild.value.instanceChildTarget,
          `nodeChild.value.instanceChildTarget not found`
        )
        const targetId = newChildrenMapToTarget.get(
          nodeChild.value.instanceChildTarget
        )
        invariant(targetId, `targetId not found`)
        const { undo: undoForThis, updatedBlock: updatedNodeChild } =
          await updateBlock(tx, {
            id: nodeChild.value.id,
            data: {
              type: nodeChild.value.type,
              instanceChildTarget: targetId,
            },
            instanceChildTarget: targetId,
            strategy: 'merge',
            path,
            isoNow,
          })
        undo.push(...undoForThis)
        treeManager.updateNode(updatedNodeChild.id, {
          value: updatedNodeChild,
        })
      })
    )
  }

  for (const collectionId of collectionsOfComponent) {
    const node = treeManager.getNode(collectionId)
    if (!node) continue
    if (isVirtualInstanceChildTarget(node.value)) continue
    if (!isListBlock(node.value)) continue
    if (node.value.typeOptions.targetBlockId === idToSourceBlockId(promoted)) {
      continue // skip the promoted one
    }

    const pathResult = tryCatch(() => {
      return treeManager.getPath(node, {
        format: 'string',
        required: true,
      })
    })

    if (!pathResult.success) {
      // we assume the collection is being deleted
      continue
    }

    const path = pathResult.result

    if (
      // @ts-ignore
      node.value.__virtual
    ) {
      continue
    }

    const { undo: undoForThis, updatedBlock: updatedCollection } =
      await updateBlock(tx, {
        id: collectionId,
        data: {
          type: 'list',
          typeOptions: {
            ...node.value.typeOptions,
            targetBlockId: idToSourceBlockId(promoted),
          },
        },
        instanceChildTarget: node.value.instanceChildTarget,
        strategy: 'merge',
        path,
        isoNow,
        ignoreHashComparison: true,
      })

    undo.push(...undoForThis)
    treeManager.updateNode(updatedCollection.id, {
      value: updatedCollection,
    })
  }

  // 4. re-target references, unions, and rich-texts to the new component
  const blocksByType = (treeManager.compoundIndexes.get('blocksByType') ??
    new Map()) as Map<BlockType, Set<string>>
  const richTextBlocks = blocksByType.get('rich-text') ?? new Set()
  const referenceBlocks = blocksByType.get('reference') ?? new Set()

  for (const id of [...richTextBlocks, ...referenceBlocks]) {
    const node = treeManager.getNode(id)
    if (!node) continue
    if (isVirtualInstanceChildTarget(node.value)) continue
    if (node.value.instanceChildTarget) continue

    const updates: any = {}
    let changed = false
    switch (node.value.type) {
      case 'rich-text': {
        const newAllowedComponents =
          node.value.constraints.allowedComponents?.map((cId) => {
            if (cId === componentSourceId) {
              changed = true
              return idToSourceBlockId(promoted)
            }
            return cId
          })
        updates.constraints = {
          allowedComponents: newAllowedComponents,
        } satisfies Partial<RichTextBlock['constraints']>
        break
      }
      case 'reference': {
        const newAcceptedTypes = node.value.constraints.acceptedTypes?.map(
          (cId) => {
            if (cId === componentSourceId) {
              changed = true
              return idToSourceBlockId(promoted)
            }
            return cId
          }
        )
        updates.constraints = {
          acceptedTypes: newAcceptedTypes,
        } satisfies Partial<ReferenceBlock['constraints']>
        break
      }
      default:
        throw new Error(`incorrect block type from index: ${node.value.type}`)
    }

    if (!changed) continue

    const path = treeManager.getPath(node, {
      format: 'string',
      required: true,
    })

    const { undo: undoForThis, updatedBlock: updatedNode } = await updateBlock(
      tx,
      {
        id,
        instanceChildTarget: node.value.instanceChildTarget,
        data: { ...updates, type: node.value.type },
        strategy: 'merge-deep',
        path,
        isoNow,
        ignoreHashComparison: true,
      }
    )

    undo.push(...undoForThis)
    treeManager.updateNode(updatedNode.id, {
      value: updatedNode,
    })
  }

  return { undo, promoted }
}

/* -------------------------------------------------------------------------------------------------
 * CHECKS
 * -----------------------------------------------------------------------------------------------*/

export const checkComponentDeletion = async (
  tx: WriteTransaction,
  {
    rootBlockId,
    component,
    ignoreValidationForBlockIds,
  }: {
    rootBlockId: string
    component: Block
    ignoreValidationForBlockIds?: string[]
  }
) => {
  ensureBaseHubTransaction(tx)
  const treeManager = await tx.getTreeManager(rootBlockId)

  let collectionTargetingIt
  let richTextTargetingIt
  let referenceTargetingIt

  const blocksByType = (treeManager.compoundIndexes.get('blocksByType') ??
    new Map()) as Map<BlockType, Set<string>>
  const instanceBlocks = blocksByType.get('instance') ?? new Set()
  const listBlocks = blocksByType.get('list') ?? new Set()

  $checkLoop: for (const bId of [...instanceBlocks, ...listBlocks]) {
    const { value: block, parentKey } = treeManager.getNode(bId, {
      required: true,
    })

    const pathResult = tryCatch(() => {
      return treeManager.getPath(block.id, {
        required: true,
      })
    })

    if (!pathResult.success) {
      continue
    }

    const path = pathResult.result

    const parentBlock = parentKey ? treeManager.getNode(parentKey)?.value : null

    if (
      (!isListBlock(block) && !isInstanceBlock(block)) ||
      block.typeOptions.targetBlockId !== idToSourceBlockId(component) ||
      ignoreValidationForBlockIds?.some((bId) => {
        return path.includes(bId)
      })
    ) {
      continue
    }

    if (isListBlock(block)) {
      if (block.typeOptions.targetBlockId === idToSourceBlockId(component)) {
        const listChildren = treeManager.getChildrenKeys(block.id)

        if (listChildren.length > 0) {
          if (listChildren.length === 1) {
            const firstChild = treeManager.getNode(listChildren[0]!)?.value
            if (
              !firstChild ||
              idToSourceBlockId(firstChild) === idToSourceBlockId(component)
            ) {
              // and list is also being deleted
              if (
                ignoreValidationForBlockIds?.includes(block.id) ||
                ignoreValidationForBlockIds?.includes(idToSourceBlockId(block))
              ) {
                continue
              }
            }
          }
          collectionTargetingIt = block
          break $checkLoop
        }
      }
    }

    if (parentBlock && isInstanceBlock(block)) {
      if (
        isRichTextBlock(parentBlock) &&
        isInstanceInRichText(parentBlock, block, treeManager.getChildrenKeys)
      ) {
        richTextTargetingIt = parentBlock
        break $checkLoop
      }
      if (isReferenceBlock(parentBlock)) {
        referenceTargetingIt = parentBlock
        break $checkLoop
      }
    }
  }

  if (collectionTargetingIt) {
    throw new ValidateStructureError(component, collectionTargetingIt)
  } else if (richTextTargetingIt) {
    throw new ValidateStructureError(component, richTextTargetingIt)
  } else if (referenceTargetingIt) {
    throw new ValidateStructureError(component, referenceTargetingIt)
  }
}

/* -------------------------------------------------------------------------------------------------
 * Errors
 * -----------------------------------------------------------------------------------------------*/

export const validateStructureError = z.object({
  type: z.literal('ValidateStructureError'),
  block: z.object({
    id: z.string(),
    title: z.string().optional(),
  }),
  referencingBlock: z.object({
    id: z.string(),
    title: z.string().optional(),
    type: blockType,
  }),
})

export const mutatorError = z.object({
  stack: z.string().optional(),
  message: z.string(),
  cause: z.discriminatedUnion('type', [validateStructureError]),
})

export type MutatorError = z.infer<typeof mutatorError>

export class ValidateStructureError extends MutatorUserError {
  cause

  constructor(block: Block, referencingBlock: Block) {
    super(
      `Can't delete block ${
        block.title || '"Untitled"'
      } because it's being used by ${referencingBlock.type}.`
    )

    this.cause = {
      type: 'ValidateStructureError',
      block: { id: idToSourceBlockId(block), title: block.title },
      referencingBlock: {
        id: idToSourceBlockId(referencingBlock),
        title: referencingBlock.title,
        type: referencingBlock.type,
      },
    }
  }
}

/* -------------------------------------------------------------------------------------------------
 * Exports
 * -----------------------------------------------------------------------------------------------*/

export {
  syncAllRichTextsUsingComponent,
  toggleMakeComponent,
  detachInstancesOfComponent,
}
